<template>
  <iModal
    :visible="isOpen"
    :open="isOpen"
    :title="modalTitle"
    :primary-action-button-label="primaryButtonLabel"
    :primary-action-button-loading="isLoading"
    :primary-action-button-disabled="networkModalMode === 'view'"
    :show-primary-action-button="networkModalMode !== 'view'"
    :max-width="600"
    @close="closeModal"
    @click:primary="handleSubmit"
  >
    <template #body>
      <iColumn gap="large">
        <template v-if="networkModalMode === 'view'">
          <iColumn width="fill">
            <iRow gap="large" vertical-align="middle" width="fill">
              <iImageContainer
                v-if="form.logoUrl"
                :src="form.logoUrl"
                :height="60"
                :width="60"
                shape="circle"
              />
              <iImageContainer
                v-if="form.splashImageUrl"
                :src="form.splashImageUrl"
                :height="100"
                width="fill"
              />
            </iRow>
            <iTextInput
              v-model="form.name"
              label="Name"
              width="fill"
              disabled
            />
            <iTextArea
              v-model="form.description"
              label="Description"
              :rows="4"
              width="fill"
              disabled
            />
            <iCheckbox v-model="form.inviteOnly" label="Invite Only" disabled />
          </iColumn>
        </template>
        <template v-else>
          <iColumn gap="none">
            <iTextInput
              v-model="form.name"
              focus
              label="Name"
              required
              placeholder="Enter network name"
              width="fill"
              @input="clearError('name')"
            />
            <iText v-if="errors.name" variant="error">
              {{ errors.name }}
            </iText>
          </iColumn>

          <iColumn gap="none">
            <iTextArea
              v-model="form.description"
              label="Description"
              required
              placeholder="Enter network description"
              :rows="4"
              width="fill"
              @input="clearError('description')"
            />
            <iText v-if="errors.description" variant="error">
              {{ errors.description }}
            </iText>
          </iColumn>

          <iCheckbox v-model="form.inviteOnly" label="Invite Only" />

          <iTextInput
            v-model="form.price"
            type="number"
            label="Monthly Price"
            :padding-left-multiplier="2"
            @input="clearError('price')"
          >
            <template #prepend>
              <iText>
                {{ currencySymbol }}
              </iText>
            </template>
          </iTextInput>
          <iText v-if="errors.price" variant="error">
            {{ errors.price }}
          </iText>

          <iColumn gap="extraSmall">
            <iLabel>Logo</iLabel>
            <iRow gap="small" vertical-align="middle">
              <iImageContainer
                v-if="form.logoUrl"
                :src="form.logoUrl"
                :height="60"
                :width="60"

                shape="circle"
              />
              <iUploader @upload-file="handleLogoSelected">
                <iButton
                  variant="secondary"
                  size="small"
                  :is-loading="isLoadingUploadLogo"
                  loading-spinner-position="inside"
                  loading-text="Uploading"
                >
                  {{ form.logoUrl ? 'Change' : 'Select' }} Logo
                </iButton>
              </iUploader>
            </iRow>
          </iColumn>

          <iColumn gap="extraSmall">
            <iLabel>Splash Image</iLabel>
            <iRow gap="small" vertical-align="middle">
              <iImageContainer
                v-if="form.splashImageUrl"
                :src="form.splashImageUrl"
                :height="100"
                width="fill"
              />
              <iUploader @upload-file="handleSplashSelected">
                <iButton
                  variant="secondary"
                  size="small"
                  :is-loading="isLoadingUploadSplashImage"
                  loading-text="Uploading"
                  loading-spinner-position="inside"
                >
                  {{ form.splashImageUrl ? 'Change' : 'Select' }} Image
                </iButton>
              </iUploader>
            </iRow>
          </iColumn>
        </template>
      </iColumn>
    </template>
  </iModal>
</template>

<script>
import { mapState, mapActions } from "pinia";
import { useNetworkStore } from "@/stores/network-store";

export default {
  name: "NetworkModal",
  data() {
    return {
      form: {
        name: "",
        description: "",
        inviteOnly: false,
        logoUrl: "",
        splashImageUrl: "",
        price: "",
      },
      errors: {
        name: "",
        description: "",
        price: "",
        uploadLogo: "",
        uploadSplashImage: "",
      },
      currencySymbol: "$",
    };
  },
  computed: {
    ...mapState(useNetworkStore, [
      "isLoadingCreateNetwork",
      "isLoadingEditNetwork",
      "isLoadingUploadLogo",
      "isLoadingUploadSplashImage",
      "networkModalOpen",
      "networkModalMode",
      "currentNetwork",
    ]),
    isOpen() {
      return this.networkModalOpen;
    },

    modalTitle() {
      const titles = {
        create: "Create Network",
        edit: "Edit Network",
        view: "Network Details",
      };
      return titles[this.networkModalMode] || titles.create;
    },
    primaryButtonLabel() {
      const labels = {
        create: "Create Network",
        edit: "Save Changes",
      };
      return labels[this.networkModalMode] || labels.create;
    },
    isLoading() {
      return this.networkModalMode === "create"
        ? this.isLoadingCreateNetwork
        : this.isLoadingEditNetwork;
    },
  },
  watch: {
    currentNetwork(newVal) {
      if (newVal) {
        this.form = {
          name: newVal.name,
          description: newVal.description,
          inviteOnly: newVal.invite_only,
          logoUrl: newVal.logo_url,
          splashImageUrl: newVal.splash_image_url,
          price: newVal.price || "",
        };
      } else {
        this.resetForm();
      }
    },
  },
  methods: {
    ...mapActions(useNetworkStore, [
      "createNetwork",
      "saveNetworkName",
      "saveNetworkDescription",
      "enableApplications",
      "disableApplications",
      "closeNetworkModal",
      "uploadLogo",
      "uploadSplashImage",
    ]),
    clearError(field) {
      this.errors[field] = "";
    },
    validate() {
      if (this.networkModalMode === "view") {
        return true;
      }
      let isValid = true;
      if (!this.form.name) {
        this.errors.name = "Network name is required";
        isValid = false;
      }
      if (!this.form.description) {
        this.errors.description = "Description is required";
        isValid = false;
      }
      if (this.form.price === "" || this.form.price === null) {
        this.errors.price = "Price is required";
        isValid = false;
      }
      return isValid;
    },
    handleLogoSelected(file) {
      this.uploadLogo(file, url => this.form.logoUrl = url);
    },
    handleSplashSelected(file) {
      this.uploadSplashImage(file, url => this.form.splashImageUrl = url);
    },
    handleSubmit() {
      if (!this.validate()) {
        return;
      }
      if (this.networkModalMode === "create") {
        this.createNetwork(
          this.form.name,
          this.form.description,
          this.form.inviteOnly,
          this.form.logoUrl,
          this.form.splashImageUrl,
          this.form.price,
          () => {
            this.closeModal();
          },
        );
      } else if (this.networkModalMode === "edit") {
        if (this.form.name !== this.currentNetwork.name) {
          this.saveNetworkName(this.currentNetwork.network_id, this.form.name);
        }
        if (this.form.description !== this.currentNetwork.description) {
          this.saveNetworkDescription(this.currentNetwork.network_id, this.form.description);
        }
        if (this.form.inviteOnly !== this.currentNetwork.invite_only) {
          if (this.form.inviteOnly) {
            this.disableApplications(this.currentNetwork.network_id);
          } else {
            this.enableApplications(this.currentNetwork.network_id);
          }
        }
      }
    },
    resetForm() {
      this.form = {
        name: "",
        description: "",
        inviteOnly: false,
        logoUrl: "",
        splashImageUrl: "",
        price: "",
      };
      this.errors = { name: "", description: "", price: "", uploadLogo: "", uploadSplashImage: "" };
    },
    closeModal() {
      this.closeNetworkModal();
      this.resetForm();
    },
  },
};
</script>

<style scoped>
</style>
